import DOMPurify from 'dompurify';

const CDATA_REGEX = /<!\[CDATA\[|\]\]>/gi;
// Only match ampersand-prefixed entities by default. Bare numeric sequences like "#8217;"
// are handled separately with stricter guards to avoid corrupting literals like CSS colors.
const HTML_ENTITY_REGEX = /&(?:#(?:x[0-9a-f]+|\d+)|[a-z0-9]+);/gi;
const BARE_NUMERIC_ENTITY_REGEX = /#(?:x[0-9a-f]+|\d+);/gi;
const NAMED_ENTITY_MAP = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: '\u00a0',
  rsquo: '’',
  lsquo: '‘',
  ldquo: '“',
  rdquo: '”',
  hellip: '…',
};

const DEFAULT_TITLE_FALLBACK = 'Untitled Article';
const DEFAULT_SUMMARY_FALLBACK = 'No summary available.';

const createDomDecoder = () => {
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
    return null;
  }
  const textarea = document.createElement('textarea');
  return (value) => {
    textarea.innerHTML = value;
    return textarea.value;
  };
};

const domDecode = createDomDecoder();

const decodeEntityMatch = (match) => {
  let entityBody = match;
  if (entityBody.startsWith('&')) {
    entityBody = entityBody.slice(1);
  }
  if (entityBody.endsWith(';')) {
    entityBody = entityBody.slice(0, -1);
  }

  const normalized = entityBody.toLowerCase();

  if (normalized.startsWith('#x')) {
    const hexCode = Number.parseInt(normalized.slice(2), 16);
    if (!Number.isNaN(hexCode)) {
      try {
        return String.fromCodePoint(hexCode);
      } catch (error) {
        return match;
      }
    }
    return match;
  }

  if (normalized.startsWith('#')) {
    const decimalCode = Number.parseInt(normalized.slice(1), 10);
    if (!Number.isNaN(decimalCode)) {
      try {
        return String.fromCodePoint(decimalCode);
      } catch (error) {
        return match;
      }
    }
    return match;
  }

  const replacement = NAMED_ENTITY_MAP[normalized];
  return replacement !== undefined ? replacement : match;
};

const replaceHtmlEntities = (value) =>
  typeof value === 'string' ? value.replace(HTML_ENTITY_REGEX, decodeEntityMatch) : value;

// Decode bare numeric entities conservatively, skipping patterns that look like CSS colors (#000; or #123456;)
const replaceBareNumericEntitiesSafely = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(BARE_NUMERIC_ENTITY_REGEX, (match) => {
    const body = match.slice(1, -1); // remove leading '#' and trailing ';'
    // Allow hex code-points like #x2019; (not CSS colors)
    if (body.toLowerCase().startsWith('x')) {
      return decodeEntityMatch(match);
    }
    // Skip 3- or 6-digit decimals which likely represent CSS hex colors adjacent to a semicolon
    if (/^\d{3}$/.test(body) || /^\d{6}$/.test(body)) {
      return match;
    }
    // Otherwise, treat as a numeric character reference
    return decodeEntityMatch(match);
  });
};

const normalizeWhitespace = (value) => value.replace(/\s+/g, ' ').trim();

export const decodeHtmlEntities = (value) => {
  if (typeof value !== 'string') {
    if (value == null) {
      return '';
    }
    value = String(value);
  }

  const stripped = value.replace(CDATA_REGEX, ' ');
  const trimmed = stripped.trim();
  if (!trimmed) {
    return '';
  }

  const decodeOnce = (input) => {
    let output = input;
    if (domDecode) {
      try {
        output = domDecode(output);
      } catch (error) {
        output = input;
      }
    }
    // First, decode standard &...; entities
    output = replaceHtmlEntities(output);
    // Then, conservatively decode bare numeric sequences (e.g., #8217;) while avoiding CSS colors
    output = replaceBareNumericEntitiesSafely(output);
    return output;
  };

  let decoded = trimmed;
  let previousValue = null;
  let passes = 0;

  while (passes < 3 && decoded !== previousValue) {
    previousValue = decoded;
    decoded = decodeOnce(decoded);
    passes += 1;
  }

  return typeof decoded === 'string'
    ? decoded.replace(/\u00a0/g, ' ').trim()
    : '';
};

const sanitizeWithDomPurify = (value, options) => {
  if (typeof DOMPurify?.sanitize === 'function') {
    return DOMPurify.sanitize(value, options);
  }
  return value;
};

const sanitizePlainText = (value) => {
  if (!value) {
    return '';
  }

  const sanitized = sanitizeWithDomPurify(value, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });

  return normalizeWhitespace(sanitized.replace(/<[^>]*>/g, ' '));
};

const sanitizeHtml = (value) => {
  if (!value) {
    return '';
  }

  const sanitized = sanitizeWithDomPurify(value, {
    USE_PROFILES: { html: true },
  });

  return typeof sanitized === 'string' ? sanitized.trim() : '';
};

export const getSafeArticleTitle = (value, fallback = DEFAULT_TITLE_FALLBACK) => {
  const decoded = decodeHtmlEntities(value);
  const safeValue = sanitizePlainText(decoded);
  if (safeValue) {
    return safeValue;
  }
  return sanitizePlainText(decodeHtmlEntities(fallback)) || DEFAULT_TITLE_FALLBACK;
};

export const getSafeArticleSummary = (value, fallback = DEFAULT_SUMMARY_FALLBACK) => {
  const decoded = decodeHtmlEntities(value);
  const safeValue = sanitizePlainText(decoded);
  if (safeValue) {
    return safeValue;
  }
  return sanitizePlainText(decodeHtmlEntities(fallback)) || DEFAULT_SUMMARY_FALLBACK;
};

export const getSafeSummaryHtml = (value, fallback = DEFAULT_SUMMARY_FALLBACK) => {
  const decoded = decodeHtmlEntities(value);
  let safeValue = sanitizeHtml(decoded);
  if (safeValue) {
    safeValue = safeValue.replace(/\]&gt;/g, '').replace(/\](?:>|&gt;)/g, '').replace(/^\]>\s*/, '');
    return safeValue;
  }
  return sanitizeHtml(decodeHtmlEntities(fallback)) || DEFAULT_SUMMARY_FALLBACK;
};

export const DEFAULT_TITLE_STANDIN = DEFAULT_TITLE_FALLBACK;
export const DEFAULT_SUMMARY_STANDIN = DEFAULT_SUMMARY_FALLBACK;

const htmlContentUtils = {
  decodeHtmlEntities,
  getSafeArticleTitle,
  getSafeArticleSummary,
  getSafeSummaryHtml,
  DEFAULT_TITLE_STANDIN,
  DEFAULT_SUMMARY_STANDIN,
};

export default htmlContentUtils;
